import {
  TrackingPageTypeWithId,
  TrackingPromotionCreative,
} from '@aboutyou/legacy-tracking-sdk'
import CdnBackgroundImage from 'components/CdnBackgroundImage'
import PromotionLink from 'components/PromotionLink'
import Button from 'components/styleguide/Button'
import { useTranslation } from 'helpers/hooks/useTranslation'
import { ElementTrackingContextProvider } from 'helpers/tracking/ElementTrackingContext'
import { PromotionImpression } from 'helpers/tracking/trackerComponents/ElementTracker'
import React, { FC, useState } from 'react'
import { useBrandById } from 'services/utils/context'
import styled, { css } from 'styled-components'
import { black } from 'uiComponents/constants/colors'
import { mediaDesktopAndUp } from 'uiComponents/constants/customMedia'
import { fsM, fsS, fwLight } from 'uiComponents/constants/typography'

interface IProps {
  brandId: string
  withRectangle?: boolean
}

const ORIGINAL_IMAGE_WIDTH = 800
const ORIGINAL_IMAGE_HEIGHT = 800

const StyledButton = styled(Button)<{ $previewHovered: boolean }>`
  border: 10px solid transparent;
  background-clip: padding-box;
  position: absolute;
  bottom: -50px;
  transition: 0.2s ease-in-out;
  transform: translateY(-50px);
  opacity: 1;

  @media ${mediaDesktopAndUp} {
    ${({ $previewHovered }) =>
      $previewHovered
        ? css`
            transform: translateY(-50px);
            opacity: 1;
          `
        : css`
            transform: translateY(0);
            opacity: 0;
          `}
  }
  :hover {
    border: 10px solid transparent !important;
    background-clip: padding-box !important;
  }
`

const Preview = styled.div`
  position: relative;
  background-color: #e9e9e9;
  overflow: hidden;

  ::before {
    content: '';
    display: block;
    padding-top: 133%;
  }
`

const Details = styled.div`
  padding: 10px;
`

const Headline = styled.div<{ $withRectangle: boolean }>`
  ${fwLight}
  letter-spacing: 0.8px;
  color: ${black};
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  text-transform: uppercase;

  ${({ $withRectangle }) =>
    $withRectangle
      ? css`
          font-size: ${fsS}px;
          line-height: 1.5;
        `
      : css`
          font-size: ${fsM}px;
          line-height: 1.4;
        `}
`

const StyledCdnBackgroundImage = styled(CdnBackgroundImage)`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: #f2f2f2;
  overflow: hidden;
  background-size: cover;
`

const BrandTile: FC<IProps> = ({ brandId, withRectangle }) => {
  const { t } = useTranslation()
  const brand = useBrandById(brandId)
  const [previewHovered, setPreviewHovered] = useState(false)

  if (!brand) {
    return null
  }

  const imageHash = brand.rectangleImageHash || brand.fallbackImageHash
  return (
    <ElementTrackingContextProvider>
      <PromotionImpression<HTMLDivElement>
        type={TrackingPageTypeWithId.Brand}
        id={brand.id}
        name={brand.name}
        url={brand.url}
        source={TrackingPromotionCreative.BrandReco}
        useIdForDestination
      >
        {({ ref, onClickCapture }) => (
          <PromotionLink onClickCapture={onClickCapture} to={brand.url}>
            <Preview
              ref={ref}
              data-testid="BrandTile"
              onMouseEnter={() => setPreviewHovered(true)}
              onMouseLeave={() => setPreviewHovered(false)}
            >
              {imageHash && (
                <StyledCdnBackgroundImage
                  imageHash={imageHash}
                  width={ORIGINAL_IMAGE_WIDTH}
                  height={ORIGINAL_IMAGE_HEIGHT}
                />
              )}
              <StyledButton
                $previewHovered={previewHovered}
                isCTA
                dataTestId="BrandTileCta"
              >
                {t('Entdecke die Marke')}
              </StyledButton>
            </Preview>
            <Details>
              <Headline $withRectangle={!!withRectangle}>{brand.name}</Headline>
            </Details>
          </PromotionLink>
        )}
      </PromotionImpression>
    </ElementTrackingContextProvider>
  )
}

export default BrandTile
